import React, { useState, useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import {
	TextField,
	IconButton,
	Paper,
	List,
	ListItem,
	ListItemText,
	CircularProgress,
	Box,
	Typography,
	Drawer,
	AppBar,
	Toolbar,
	Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CloseIcon from "@mui/icons-material/Close";
import RoomIcon from "@mui/icons-material/Room";
import styles from "./SearchBar.module.css";

interface SearchResult {
	id: string;
	place_name: string;
	center: [number, number];
}

interface DirectionsStep {
	maneuver: {
		instruction: string;
	};
	distance: number;
	duration: number;
}

interface Props {
	map: React.MutableRefObject<mapboxgl.Map | null>;
}

const StyledTextField = styled(TextField)({
	"& .MuiOutlinedInput-root": {
		backgroundColor: "white",
		borderRadius: "8px",
		"& fieldset": {
			borderColor: "rgba(0, 0, 0, 0.1)",
		},
		"&:hover fieldset": {
			borderColor: "rgba(0, 0, 0, 0.2)",
		},
		"&.Mui-focused fieldset": {
			borderColor: "#3887be",
		},
	},
});

const SearchBar: React.FC<Props> = ({ map }) => {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<SearchResult[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showResults, setShowResults] = useState(false);
	const [directionsSteps, setDirectionsSteps] = useState<DirectionsStep[]>([]);
	const [loading, setLoading] = useState(false);
	const [showDirections, setShowDirections] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState<SearchResult | null>(
		null
	);

	const searchTimeout = useRef<NodeJS.Timeout | null>(null);
	const currentMarker = useRef<mapboxgl.Marker | null>(null);
	const destinationMarker = useRef<mapboxgl.Marker | null>(null);
	const routeSource = useRef<mapboxgl.GeoJSONSource | null>(null);

	// Initialize route layer
	useEffect(() => {
		if (map?.current) {
			const initializeRoute = () => {
				if (!map.current) {
					console.error("Map is not initialized");
					return;
				}

				if (!map.current?.getSource("route")) {
					map.current.addSource("route", {
						type: "geojson",
						data: {
							type: "Feature",
							properties: {},
							geometry: {
								type: "LineString",
								coordinates: [],
							},
						},
					});

					map.current.addLayer({
						id: "route",
						type: "line",
						source: "route",
						layout: {
							"line-join": "round",
							"line-cap": "round",
						},
						paint: {
							"line-color": "#3887be",
							"line-width": 5,
							"line-opacity": 0.75,
						},
					});

					routeSource.current = map.current.getSource(
						"route"
					) as mapboxgl.GeoJSONSource;
				}
			};

			if (map.current.loaded()) {
				initializeRoute();
			} else {
				map.current.on("load", initializeRoute);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map.current, routeSource.current]);

	const removeMarkers = () => {
		if (currentMarker.current) {
			currentMarker.current.remove();
			currentMarker.current = null;
		}

		if (destinationMarker.current) {
			destinationMarker.current.remove();
			destinationMarker.current = null;
		}
	};

	const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setQuery(value);
		setShowResults(true);
		setShowDirections(false);

		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		if (!value.trim()) {
			setResults([]);
			return;
		}

		setIsLoading(true);
		searchTimeout.current = setTimeout(async () => {
			try {
				const response = await fetch(
					`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
						value
					)}.json?access_token=${mapboxgl.accessToken}`
				);
				const data = await response.json();
				setResults(data.features);
			} catch (error) {
				console.error("Error searching:", error);
			} finally {
				setIsLoading(false);
			}
		}, 300);
	};

	const getDirections = async (
		start: [number, number],
		end: [number, number]
	) => {
		try {
			const response = await fetch(
				`https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?steps=true&geometries=geojson&access_token=${mapboxgl.accessToken}`
			);
			const data = await response.json();

			if (data.routes && data.routes[0]) {
				const route = data.routes[0];
				setDirectionsSteps(route.legs[0].steps);

				if (routeSource.current && map?.current) {
					routeSource.current.setData({
						type: "Feature",
						properties: {},
						geometry: route.geometry,
					});

					const coordinates = route.geometry.coordinates;
					const bounds = coordinates.reduce(
						(bounds: mapboxgl.LngLatBounds, coord: [number, number]) => {
							return bounds.extend(coord);
						},

						new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
					);

					map.current.fitBounds(bounds, {
						padding: 50,
					});
				}

				setShowDirections(true);
				setLoading(false);
			}
		} catch (error) {
			console.error("Error getting directions:", error);
			setLoading(false);
		}
	};

	const handleSelectLocation = (result: SearchResult) => {
		setSelectedLocation(result);

		if (map?.current) {
			map.current.flyTo({
				center: result.center,
				zoom: 20,
				essential: true,
			});

			// Add destination marker
			removeMarkers();
			const markerElement = document.createElement("div");
			markerElement.className = styles.mapMarker;
			markerElement.innerHTML = "📍";
			destinationMarker.current = new mapboxgl.Marker(markerElement)
				.setLngLat(result.center)
				.addTo(map.current);
		}

		setQuery(result.place_name);
		setShowResults(false);
		setResults([]);
	};

	const handleGetDirections = () => {
		if (!selectedLocation) {
			console.error("No destination selected");
			return;
		}

		setShowResults(false);
		routeSource.current?.setData({
			type: "Feature",
			properties: {},
			geometry: {
				type: "LineString",
				coordinates: [],
			},
		});

		if ("geolocation" in navigator) {
			setLoading(true);

			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					const currentCoords: [number, number] = [longitude, latitude];

					// Add current location marker
					if (map?.current) {
						const originElement = document.createElement("div");
						originElement.className = styles.mapMarker;
						originElement.innerHTML = "🚗";
						currentMarker.current = new mapboxgl.Marker(originElement)
							.setLngLat(currentCoords)
							.addTo(map.current);

						getDirections(currentCoords, selectedLocation.center);
					}
				},
				(error) => {
					console.error("Error getting location:", error);
				}
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			if (!target.closest(`.${styles.searchContainer}`)) {
				setShowResults(false);
			}
		};

		document.addEventListener("click", handleClickOutside);
		return () => {
			document.removeEventListener("click", handleClickOutside);
			removeMarkers();
		};
	}, []);

	return (
		<Box className={styles.searchContainer}>
			{showResults && (results.length > 0 || isLoading) && (
				<Paper elevation={3} sx={{ mt: 1, maxHeight: 300, overflow: "auto" }}>
					<List>
						{isLoading ? (
							<ListItem>
								<Box display='flex' justifyContent='center' width='100%' py={2}>
									<CircularProgress size={24} />
								</Box>
							</ListItem>
						) : (
							results.map((result) => (
								<ListItem
									key={result.id}
									// type='button'
									onClick={() => handleSelectLocation(result)}
								>
									<ListItemText primary={result.place_name} />
								</ListItem>
							))
						)}
					</List>
				</Paper>
			)}

			<Drawer
				anchor='left'
				open={showDirections}
				onClose={() => setShowDirections(false)}
				PaperProps={{
					sx: {
						width: 350,
						mt: "64px", // Height of AppBar
						height: "calc(100% - 64px)",
					},
				}}
			>
				<AppBar position='sticky' color='inherit' elevation={0}>
					<Toolbar>
						<Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
							Directions
						</Typography>
						<IconButton onClick={() => setShowDirections(false)}>
							<CloseIcon />
						</IconButton>
					</Toolbar>
				</AppBar>

				<Divider />

				<List sx={{ p: 0 }}>
					{directionsSteps.map((step, index) => (
						<ListItem key={index} divider>
							<Box display='flex' gap={2}>
								<Box
									sx={{
										width: 24,
										height: 24,
										borderRadius: "50%",
										bgcolor: "primary.main",
										color: "white",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
									}}
								>
									{index + 1}
								</Box>
								<Box>
									<Typography>{step.maneuver.instruction}</Typography>
									<Typography variant='body2' color='text.secondary'>
										{(step.distance / 1000).toFixed(1)} km ·{" "}
										{Math.round(step.duration / 60)} min
									</Typography>
								</Box>
							</Box>
						</ListItem>
					))}
				</List>
			</Drawer>

			<Box className={styles.searchInputWrapper}>
				<StyledTextField
					fullWidth
					value={query}
					onChange={handleSearch}
					onKeyDown={(e) => {
						if (e.key === "Enter" && results.length > 0) {
							handleSelectLocation(results[0]);
						}
					}}
					placeholder='Search destination...'
					size='small'
					InputProps={{
						startAdornment: <RoomIcon sx={{ mr: 1, color: "action.active" }} />,
					}}
				/>

				<IconButton
					onClick={handleGetDirections}
					disabled={!selectedLocation || loading}
					color='primary'
					sx={{
						bgcolor: "white",
						"&:hover": { bgcolor: "grey.100" },
						boxShadow: 1,
					}}
				>
					{loading ? <CircularProgress size={24} /> : <DirectionsCarIcon />}
				</IconButton>
			</Box>
		</Box>
	);
};

export default SearchBar;
